import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  FormGroup,
} from "reactstrap";
import { 
  getNextReceiptNumber, 
  saveReceipt, 
  getReceipts,
} from "../../components/Api/GmsReceipt";
import { checkDayClosing, getDailyClosingOpeningBalance, getDailyClosingSummary } from "../../components/Api/GmsDayClosing";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getBanks } from "../../components/Api/GmsBank";
import { getBankAccounts } from "../../components/Api/GmsBankAccount";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission,
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { formatDateForAPI, formatDateForDisplay } from "../../helpers/dateHelper";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    maxHeight: 200,
  }),
  menuList: (provided) => ({
    ...provided,
    maxHeight: 180,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

// Session management
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    companyId: authUser.companyId || user.user_Company_Id || null,
    branchId: authUser.branchId || user.user_Branch_Id || null,
  };
};

const defaultForm = {
  ReceiptId: 0,
  ReceiptNumber: "",
  ReceiptDate: new Date(),
  Amount: 0,
  DepositAmount: 0,
  PaymentType: "ca",
  Type: "cd",
  Is_Advance: false,
  BusinessId: null,
  BranchId: null,
  BankId: null,
  BankAccountId: null,
  CreatedBy: 1,
  ReceiptDetails: [],
};

export default function AddCashDepositPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(defaultForm);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [amountToBeDeposited, setAmountToBeDeposited] = useState(0);
  const [loadingAmount, setLoadingAmount] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [bankSearchInput, setBankSearchInput] = useState("");
  const [bankAccountSearchInput, setBankAccountSearchInput] = useState("");

  // Permission states
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsAddCashDeposit');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsAddCashDeposit');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsAddCashDeposit');
  const isAdmin = isUserAdmin();

  // Get user session
  const session = useMemo(() => getUserSession(), []);

  // Check if business/branch should be readonly
  const isBusinessReadonly = !!session.companyId;
  const isBranchReadonly = !!session.branchId;

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
      
      // Set business/branch from session if available
      if (session.companyId) {
        setForm(prev => ({ ...prev, BusinessId: session.companyId }));
      }
      if (session.branchId) {
        setForm(prev => ({ ...prev, BranchId: session.branchId }));
      }
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Check day closing after business/branch is set from session
  useEffect(() => {
    if (hasPermission) {
      const timer = setTimeout(() => {
        checkCurrentDateDayClosing();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [hasPermission, session.companyId, session.branchId]);

  // Load amount to be deposited when branch/date changes (only after branch is selected)
  useEffect(() => {
    if (form.BusinessId && form.BranchId && form.ReceiptDate) {
      calculateAmountToBeDeposited();
    } else {
      setAmountToBeDeposited(0);
      setForm(prev => ({ ...prev, DepositAmount: 0 }));
    }
  }, [form.BusinessId, form.BranchId, form.ReceiptDate]);

  // Load receipt number when business is selected
  useEffect(() => {
    if (form.BusinessId) {
      loadNextReceiptNumber();
    }
  }, [form.BusinessId]);

  // Load bank accounts when bank is selected
  useEffect(() => {
    if (form.BankId) {
      loadBankAccounts();
    } else {
      setBankAccounts([]);
      setForm(prev => ({ ...prev, BankAccountId: null }));
    }
  }, [form.BankId]);

  // Update deposit amount when amount to be deposited changes
  useEffect(() => {
    setForm(prev => ({ ...prev, DepositAmount: amountToBeDeposited }));
  }, [amountToBeDeposited]);

  // Check if current date is closed/holiday and previous day closing
  const checkCurrentDateDayClosing = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const businessId = session.companyId || form.BusinessId;
      const branchId = session.branchId || form.BranchId || null;
      
      if (businessId) {
        const checkRequest = {
          transactionDate: formatDateForAPI(today, false),
          businessId: businessId,
          branchId: branchId
        };
        
        const dayClosingCheck = await checkDayClosing(checkRequest);
        
        if (dayClosingCheck.isClosed) {
          const message = dayClosingCheck.isHoliday 
            ? "Holiday has been marked for today. Please come back tomorrow."
            : "Day closing has been marked for today. Please come back tomorrow.";
          
          Swal.fire({
            icon: 'warning',
            title: 'Day Closed',
            text: message,
            confirmButtonText: 'OK'
          });
          
          setIsReadOnly(true);
          return;
        }
        
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        const previousDayCheck = await checkDayClosing({
          transactionDate: formatDateForAPI(yesterday, false),
          businessId: businessId,
          branchId: branchId
        });
        
        // Only validate previous day closing if there are existing day closing records
        // If hasAnyDayClosing is false, it means first time use, so skip validation
        if (!previousDayCheck.isClosed && previousDayCheck.hasAnyDayClosing !== false) {
          const yesterdayStr = formatDateForDisplay(yesterday, 'date');
          Swal.fire({
            icon: 'warning',
            title: 'Previous Day Not Closed',
            text: `Day closing is not done for ${yesterdayStr}. Please close the previous day first before creating any transactions.`,
            confirmButtonText: 'OK'
          });
          
          setIsReadOnly(true);
        }
      }
    } catch (error) {
      console.error("Error checking day closing on page load:", error);
    }
  };

  // Calculate amount to be deposited: Last day closing CashTotal + cash from sales/receipts till current time - already deposited today
  const calculateAmountToBeDeposited = async () => {
    try {
      setLoadingAmount(true);
      const receiptDate = form.ReceiptDate instanceof Date ? form.ReceiptDate : new Date(form.ReceiptDate);
      const dateOnly = new Date(receiptDate);
      dateOnly.setHours(0, 0, 0, 0);
      
      // Get opening balance (last day closing cash total)
      const openingBalanceData = await getDailyClosingOpeningBalance({
        businessId: form.BusinessId,
        branchId: form.BranchId || null,
        upToDate: formatDateForAPI(dateOnly, false)
      });
      
      const openingCashTotal = openingBalanceData?.cashTotal || 0;
      
      // Get current day cash summary (only cash transactions, excluding cash deposits)
      const summaryData = await getDailyClosingSummary({
        closingDate: formatDateForAPI(dateOnly, false),
        businessId: form.BusinessId,
        branchId: form.BranchId || null,
        transactionType: 'CASH'
      });
      
      const currentDayCashTotal = summaryData?.cashTotal || 0;
      
      // Get already deposited amounts today (cash deposits Type='cd' for same business/branch/date)
      const todayDepositsFilter = {
        Type: "cd",
        BusinessId: form.BusinessId,
        BranchId: form.BranchId || null,
        FromDate: formatDateForAPI(dateOnly, false),
        ToDate: formatDateForAPI(dateOnly, false),
        Page: 1,
        PageSize: 1000 // Get all deposits for today
      };
      
      const todayDepositsResponse = await getReceipts(todayDepositsFilter);
      const todayDeposits = todayDepositsResponse?.receipts || [];
      
      // Sum all deposits made today
      const alreadyDepositedToday = todayDeposits.reduce((sum, deposit) => {
        const amount = deposit.amount || deposit.Amount || 0;
        return sum + amount;
      }, 0);
      
      // Calculate total: opening balance + current day cash - already deposited today
      const total = openingCashTotal + currentDayCashTotal - alreadyDepositedToday;
      
      // Ensure total is not negative
      setAmountToBeDeposited(Math.max(0, total));
    } catch (error) {
      console.error("Error calculating amount to be deposited:", error);
      Swal.fire("Error", "Failed to calculate amount to be deposited", "error");
      setAmountToBeDeposited(0);
    } finally {
      setLoadingAmount(false);
    }
  };

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadBusinesses(),
        loadBranches(),
        loadBanks(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ status: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
    }
  };

  const loadBanks = async () => {
    try {
      const response = await getBanks({ status: true, page: 1, pageSize: 1000 });
      setBanks(response?.banks || []);
    } catch (error) {
      console.error("Error loading banks:", error);
    }
  };

  const loadBankAccounts = async () => {
    try {
      if (!form.BankId) {
        setBankAccounts([]);
        return;
      }
      const response = await getBankAccounts({ 
        bankId: form.BankId, 
        status: true, 
        page: 1, 
        pageSize: 1000 
      });
      setBankAccounts(response?.bankAccounts || []);
    } catch (error) {
      console.error("Error loading bank accounts:", error);
      setBankAccounts([]);
    }
  };

  const loadNextReceiptNumber = async () => {
    try {
      const response = await getNextReceiptNumber(form.BusinessId);
      if (response.isSuccess) {
        setForm(prev => ({ ...prev, ReceiptNumber: response.nextReceiptNumber }));
      }
    } catch (error) {
      console.error("Error loading next receipt number:", error);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!form.BusinessId) {
        Swal.fire("Error", "Please select a company", "error");
        return;
      }
      if (!form.BankAccountId) {
        Swal.fire("Error", "Please select a bank account", "error");
        return;
      }
      if (!form.DepositAmount || form.DepositAmount <= 0) {
        Swal.fire("Error", "Deposit amount must be greater than 0", "error");
        return;
      }
      if (form.DepositAmount > amountToBeDeposited) {
        Swal.fire("Error", `Deposit amount cannot exceed amount to be deposited (${amountToBeDeposited.toFixed(2)})`, "error");
        return;
      }

      // Check day closing before saving
      if (form.BusinessId && form.ReceiptDate) {
        try {
          const receiptDate = new Date(form.ReceiptDate);
          const checkRequest = {
            transactionDate: formatDateForAPI(receiptDate, false),
            businessId: form.BusinessId,
            branchId: form.BranchId || null
          };
          
          const dayClosingCheck = await checkDayClosing(checkRequest);
          
          if (dayClosingCheck.isClosed) {
            const message = dayClosingCheck.isHoliday 
              ? "Holiday has been marked for this date. Please come back tomorrow."
              : "Day closing has been marked for this date. Please come back tomorrow.";
            Swal.fire("Day Closed", message, "warning");
            return;
          }
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const receiptDateOnly = new Date(receiptDate);
          receiptDateOnly.setHours(0, 0, 0, 0);
          
          if (receiptDateOnly >= today) {
            const previousDate = new Date(receiptDateOnly);
            previousDate.setDate(previousDate.getDate() - 1);
            
            const previousDayCheck = await checkDayClosing({
              transactionDate: formatDateForAPI(previousDate, false),
              businessId: form.BusinessId,
              branchId: form.BranchId || null
            });
            
            // Only validate previous day closing if there are existing day closing records
            // If hasAnyDayClosing is false, it means first time use, so skip validation
            if (!previousDayCheck.isClosed && previousDayCheck.hasAnyDayClosing !== false) {
              Swal.fire("Previous Day Not Closed", `Day closing is not done for ${formatDateForDisplay(previousDate, 'date')}. Please close the previous day first.`, "warning");
              return;
            }
          }
        } catch (error) {
          console.error("Error checking day closing:", error);
        }
      }

      setSaveLoading(true);

      const selectedDate = form.ReceiptDate instanceof Date ? form.ReceiptDate : new Date(form.ReceiptDate);
      const now = new Date();
      const receiptDateTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds()
      );

      // Use FormData to match the controller's [FromForm] expectation
      const formData = new FormData();
      formData.append('ReceiptId', form.ReceiptId);
      formData.append('ReceiptNumber', form.ReceiptNumber);
      formData.append('ReceiptDate', formatDateForAPI(receiptDateTime, true));
      formData.append('Amount', form.DepositAmount);
      formData.append('PaymentType', "ca");
      // AuthCode not needed for cash deposits, so we don't append it
      formData.append('Type', "cd");
      formData.append('Is_Advance', false);
      formData.append('BusinessId', form.BusinessId);
      if (form.BranchId) formData.append('BranchId', form.BranchId);
      // CustomerId not needed for cash deposits
      formData.append('BankAccountId', form.BankAccountId);
      formData.append('CreatedBy', session.userId);
      
      // Send ReceiptDetails as empty array JSON string
      formData.append('receiptDetailsJson', JSON.stringify([]));

      const result = await saveReceipt(formData);

      if (result.isSuccess) {
        Swal.fire({
          icon: 'success',
          title: 'Success',
          text: result.message || 'Cash deposit saved successfully',
          confirmButtonText: 'OK'
        }).then(() => {
          // Reset form and stay on the same page
          setForm({
            ...defaultForm,
            BusinessId: session.companyId || null,
            BranchId: session.branchId || null,
            ReceiptDate: new Date(),
          });
          setBankSearchInput("");
          setBankAccountSearchInput("");
          loadNextReceiptNumber();
          calculateAmountToBeDeposited();
        });
      } else {
        Swal.fire("Error", result.message || "Failed to save cash deposit", "error");
      }
    } catch (error) {
      console.error("Error saving cash deposit:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to save cash deposit";
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const businessOptions = useMemo(() => {
    return businesses.map(b => ({
      value: b.business_Id,
      label: b.business_NameEn || b.business_Name,
    }));
  }, [businesses]);

  const branchOptions = useMemo(() => {
    if (!form.BusinessId) return [];
    return branches
      .filter(b => b.branch_Buisness_Id === form.BusinessId)
      .map(b => ({
        value: b.branch_Id,
        label: `${b.branch_Code || ''} - ${b.branch_NameEn || ''}`.trim(),
      }));
  }, [branches, form.BusinessId]);

  const bankOptions = useMemo(() => {
    if (!bankSearchInput) {
      return banks.map(b => {
        const bankId = b.bankId || b.BankId;
        const bankCode = b.bankCode || b.BankCode || '';
        const bankNameEn = b.bankNameEn || b.BankNameEn || '';
        return {
          value: bankId,
          label: `${bankCode} - ${bankNameEn}`.trim(),
        };
      });
    }
    const searchLower = bankSearchInput.toLowerCase();
    return banks
      .filter(b => {
        const bankCode = (b.bankCode || b.BankCode || '').toLowerCase();
        const bankNameEn = (b.bankNameEn || b.BankNameEn || '').toLowerCase();
        const bankNameAr = (b.bankNameAr || b.BankNameAr || '').toLowerCase();
        return bankCode.includes(searchLower) || bankNameEn.includes(searchLower) || bankNameAr.includes(searchLower);
      })
      .map(b => {
        const bankId = b.bankId || b.BankId;
        const bankCode = b.bankCode || b.BankCode || '';
        const bankNameEn = b.bankNameEn || b.BankNameEn || '';
        return {
          value: bankId,
          label: `${bankCode} - ${bankNameEn}`.trim(),
        };
      });
  }, [banks, bankSearchInput]);

  const bankAccountOptions = useMemo(() => {
    if (!form.BankId) return [];
    const accounts = bankAccounts.filter(ba => {
      const bankId = ba.bankId || ba.BankId;
      return bankId === form.BankId;
    });
    if (!bankAccountSearchInput) {
      return accounts.map(ba => {
        const accountNumber = ba.accountNumber || ba.AccountNumber || '';
        const accountHolderName = ba.accountHolderName || ba.AccountHolderName || '';
        const bankAccountId = ba.bankAccountId || ba.BankAccountId;
        return {
          value: bankAccountId,
          label: `${accountNumber} - ${accountHolderName}`,
        };
      });
    }
    const searchLower = bankAccountSearchInput.toLowerCase();
    return accounts
      .filter(ba => {
        const accountNumber = (ba.accountNumber || ba.AccountNumber || '').toLowerCase();
        const accountHolderName = (ba.accountHolderName || ba.AccountHolderName || '').toLowerCase();
        return accountNumber.includes(searchLower) || accountHolderName.includes(searchLower);
      })
      .map(ba => {
        const accountNumber = ba.accountNumber || ba.AccountNumber || '';
        const accountHolderName = ba.accountHolderName || ba.AccountHolderName || '';
        const bankAccountId = ba.bankAccountId || ba.BankAccountId;
        return {
          value: bankAccountId,
          label: `${accountNumber} - ${accountHolderName}`,
        };
      });
  }, [bankAccounts, form.BankId, bankAccountSearchInput]);

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toFixed(2);
  };

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Add Cash Deposit" breadcrumbItem="Add Cash Deposit" />
          
          <Row>
            <Col xs={12}>
              <Card>
                <CardBody>
                  {hasEditOrHigher ? (
                    <>
                      <h5 className="card-title mb-4">Cash Deposit</h5>
                      
                      <Form>
                        <Row>
                          <Col md={3}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Company <span className="text-danger">*</span></Label>
                              <Select
                                value={businessOptions.find(opt => opt.value === form.BusinessId) || null}
                                onChange={(option) => {
                                  handleFormChange("BusinessId", option?.value || null);
                                  handleFormChange("BranchId", null);
                                }}
                                options={businessOptions}
                                isClearable={!isBusinessReadonly}
                                isDisabled={isBusinessReadonly || isReadOnly}
                                placeholder="Select Company"
                                styles={selectStyles}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Branch</Label>
                              <Select
                                value={branchOptions.find(opt => opt.value === form.BranchId) || null}
                                onChange={(option) => handleFormChange("BranchId", option?.value || null)}
                                options={branchOptions}
                                isClearable={!isBranchReadonly}
                                isDisabled={isBranchReadonly || !form.BusinessId || isReadOnly}
                                placeholder="Select Branch"
                                styles={selectStyles}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Date <span className="text-danger">*</span></Label>
                              <DatePicker
                                selected={form.ReceiptDate}
                                onChange={(date) => handleFormChange("ReceiptDate", date)}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={new Date()}
                                disabled={isReadOnly}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={3}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Receipt Number</Label>
                              <Input
                                type="text"
                                value={form.ReceiptNumber}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Amount to be Deposited</Label>
                              <Input
                                type="text"
                                value={!form.BranchId ? "" : (loadingAmount ? "Calculating..." : formatCurrency(amountToBeDeposited))}
                                readOnly
                                style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}
                                disabled={!form.BranchId}
                              />
                              {loadingAmount && form.BranchId && (
                                <div className="mt-2">
                                  <ClipLoader size={16} color="#556ee6" />
                                </div>
                              )}
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Deposit <span className="text-danger">*</span></Label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max={amountToBeDeposited}
                                value={form.DepositAmount || ''}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  if (value > amountToBeDeposited) {
                                    Swal.fire({
                                      icon: 'warning',
                                      title: 'Invalid Amount',
                                      text: `Deposit amount cannot exceed amount to be deposited (${formatCurrency(amountToBeDeposited)})`,
                                    });
                                    return;
                                  }
                                  handleFormChange("DepositAmount", value);
                                }}
                                disabled={!form.BranchId || isReadOnly}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Bank Name <span className="text-danger">*</span></Label>
                              <Select
                                value={bankOptions.find(opt => opt.value === form.BankId) || null}
                                onChange={(option) => {
                                  handleFormChange("BankId", option?.value || null);
                                  handleFormChange("BankAccountId", null);
                                }}
                                options={bankOptions}
                                isClearable
                                isSearchable
                                isDisabled={isReadOnly}
                                placeholder="Search bank..."
                                styles={selectStyles}
                                onInputChange={(inputValue) => {
                                  setBankSearchInput(inputValue);
                                  return inputValue;
                                }}
                                filterOption={() => true}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup className="mb-3">
                              <Label className="mb-2">Bank Account Number <span className="text-danger">*</span></Label>
                              <Select
                                value={bankAccountOptions.find(opt => opt.value === form.BankAccountId) || null}
                                onChange={(option) => handleFormChange("BankAccountId", option?.value || null)}
                                options={bankAccountOptions}
                                isClearable
                                isSearchable
                                isDisabled={!form.BankId || isReadOnly}
                                placeholder={form.BankId ? "Search account..." : "Select bank first"}
                                styles={selectStyles}
                                onInputChange={(inputValue) => {
                                  setBankAccountSearchInput(inputValue);
                                  return inputValue;
                                }}
                                filterOption={() => true}
                              />
                            </FormGroup>
                          </Col>
                        </Row>

                        <Row className="mt-4">
                          <Col>
                            <Button
                              color="primary"
                              onClick={handleSave}
                              disabled={saveLoading || !form.BusinessId || !form.BankAccountId || !form.DepositAmount || form.DepositAmount <= 0 || isReadOnly}
                            >
                              {saveLoading && <ClipLoader size={16} color="#ffffff" className="me-2" />}
                              Save
                            </Button>
                            <Button
                              color="secondary"
                              className="ms-2"
                              onClick={() => {
                                setForm({
                                  ...defaultForm,
                                  BusinessId: session.companyId || null,
                                  BranchId: session.branchId || null,
                                  ReceiptDate: new Date(),
                                });
                                setBankSearchInput("");
                                setBankAccountSearchInput("");
                                loadNextReceiptNumber();
                                calculateAmountToBeDeposited();
                              }}
                            >
                              Clear
                            </Button>
                            <Button
                              color="info"
                              className="ms-2"
                              onClick={() => navigate('/GmsCashDeposit')}
                            >
                              Back to List
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <p>You don't have permission to add cash deposits.</p>
                      <Button color="primary" onClick={() => navigate('/GmsCashDeposit')}>
                        Back to List
                      </Button>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}

